import { addMiddleware } from 'mobx-state-tree';
import type { RaeditorStore } from '@reyka/openpolotno';

const injectedCss = new Set<string>();
const patchedStores = new WeakSet<object>();

/**
 * After loadJSON: register real Google Fonts faces in the document so canvas
 * text is not Times/serif fallback.
 *
 * OpenPolotno quirk: `{ fontFamily }` stubs in store.fonts make loadFont call
 * injectCustomFont (no-op) instead of injectGoogleFont. We do not strip those
 * or write element props (that crashed/blanked the editor). We only inject
 * css2 @font-face rules and await document.fonts.
 */
function collectFamilies(store: RaeditorStore): Set<string> {
  const families = new Set<string>();

  try {
    const listed = (store as { fonts?: Array<{ fontFamily?: string }> }).fonts;
    if (Array.isArray(listed)) {
      for (const f of listed) {
        if (f?.fontFamily) families.add(f.fontFamily);
      }
    }
  } catch {
    /* ignore */
  }

  try {
    const json = store.toJSON?.() as {
      fonts?: Array<{ fontFamily?: string }>;
      pages?: Array<{ children?: Array<{ type?: string; fontFamily?: string }> }>;
    };
    for (const f of json?.fonts || []) {
      if (f?.fontFamily) families.add(f.fontFamily);
    }
    for (const page of json?.pages || []) {
      for (const child of page.children || []) {
        if (child?.type === 'text' && child.fontFamily) {
          families.add(child.fontFamily);
        }
      }
    }
  } catch {
    /* ignore */
  }

  return families;
}

function injectGoogleCss2(family: string): void {
  if (typeof document === 'undefined') return;
  if (injectedCss.has(family)) return;
  const id = `design-studio-gf-${family.replace(/[^a-zA-Z0-9]+/g, '-')}`;
  if (document.getElementById(id)) {
    injectedCss.add(family);
    return;
  }
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  const familyParam = family.replace(/ /g, '+');
  link.href = `https://fonts.googleapis.com/css2?family=${familyParam}:wght@400;700&display=swap`;
  document.head.appendChild(link);
  injectedCss.add(family);
}

async function waitForFamily(family: string): Promise<void> {
  if (typeof document === 'undefined' || !document.fonts?.load) return;
  for (const spec of [`400 64px "${family}"`, `normal 64px "${family}"`, `700 64px "${family}"`]) {
    try {
      await document.fonts.load(spec);
    } catch {
      /* face may not exist */
    }
  }
}


/**
 * OpenPolotno letterSpacing is an em fraction (UI stores v/100, ~ -0.5..2.5).
 * Imports / Gemini often write percent-like values (2, 4, 6) which render as
 * multi-em tracking and clip tracked headlines (ANNUAL → "A N N U").
 */
export function normalizeTextLetterSpacing(store: RaeditorStore): void {
  try {
    const pages = (store as { pages?: Array<{ children?: Array<Record<string, unknown>> }> }).pages;
    if (!Array.isArray(pages)) return;
    for (const page of pages) {
      const children = page?.children;
      if (!Array.isArray(children)) continue;
      for (const el of children) {
        if (!el || el.type !== 'text') continue;
        const ls = el.letterSpacing;
        if (typeof ls !== 'number' || !Number.isFinite(ls)) continue;
        if (Math.abs(ls) <= 2.5) continue;
        const fontSize = typeof el.fontSize === 'number' && el.fontSize > 0 ? el.fontSize : 16;
        // Percent-like (common) or raw px
        const next = Math.abs(ls) <= 250 ? ls / 100 : ls / fontSize;
        try {
          const setter = (el as { set?: (p: { letterSpacing: number }) => void }).set;
          if (typeof setter === 'function') setter.call(el, { letterSpacing: next });
          else (el as { letterSpacing: number }).letterSpacing = next;
        } catch {
          /* MST may block — ignore */
        }
      }
    }
  } catch (err) {
    console.warn('normalizeTextLetterSpacing skipped', err);
  }
}

export async function ensureStoreFonts(store: RaeditorStore): Promise<void> {
  const families = collectFamilies(store);
  if (families.size === 0) return;

  for (const family of families) {
    injectGoogleCss2(family);
  }

  await new Promise((r) => setTimeout(r, 80));

  const load = (store as { loadFont?: (family: string) => unknown }).loadFont;

  await Promise.all(
    [...families].map(async (family) => {
      await waitForFamily(family);
      if (typeof load !== 'function') return;
      try {
        await Promise.resolve(load.call(store, family));
      } catch (err) {
        console.warn('loadFont failed', family, err);
      }
    }),
  );
}

/**
 * Hook loadJSON via MST middleware — never reassign store.loadJSON (MST proxies
 * reject that and white-screen the app).
 */
export function patchStoreLoadJsonFonts(store: RaeditorStore): void {
  try {
    if (patchedStores.has(store as object)) return;
    addMiddleware(store as never, (call, next) => {
      const result = next(call);
      if (call.name === 'loadJSON') {
        normalizeTextLetterSpacing(store);
        void ensureStoreFonts(store);
      }
      return result;
    });
    patchedStores.add(store as object);
  } catch (err) {
    console.warn('patchStoreLoadJsonFonts skipped', err);
  }
}
